import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setSessionExpired } from "../redux/sessionSlice";
import { getStoredValue, setInBothStorages } from "../services/logics";
import { reportSessionExpiredShown } from "../utils/sessionTelemetry";

// Auth pages must stay reachable while the session is expired - they are how the user gets back in.
const AUTH_PAGES = ["/signin", "/signup", "/admin-signin", "/forgot-password", "/reset-password", "/verify-email"];

// Query param Signin.jsx reads to show a "you were signed out" message after this redirect.
export const SESSION_EXPIRED_QUERY_PARAM = "session_expired";

/**
 * Once the session is known to be dead, log the user out and hard-redirect to /signin instead of
 * rendering an interstitial "Session Has Expired" screen in place of the app.
 *
 * The app must not stay mounted underneath: something in the user's browser (an overlay/cookie-banner
 * remover) could previously delete a fixed full-screen overlay and leave a fully interactive page
 * whose every click silently 401'd - error toasts are suppressed while the session is expired, so the
 * user saw nothing at all. A hard navigation away from the page removes that risk entirely: there is
 * nothing left mounted for anything to delete.
 */
const SessionExpiredGate = ({ children }) => {
  const isSessionExpired = useSelector(state => state.session.isSessionExpired);
  const dispatch = useDispatch();

  // SP sessions end at a fixed, known time (session_expires_at from the backend). Expire on the
  // clock instead of waiting for the first request to fail. Re-checked on focus/visibility too,
  // because browsers throttle timers in background tabs.
  useEffect(() => {
    if (isSessionExpired) return undefined;
    const check = () => {
      const deadline = Date.parse(getStoredValue("session_expires_at") || "");
      if (getStoredValue("isLoggedIn") === "true" && !Number.isNaN(deadline) && Date.now() >= deadline) {
        dispatch(setSessionExpired(true));
      }
    };
    check();
    const timer = setInterval(check, 30000);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [isSessionExpired, dispatch]);

  const shouldRedirect = isSessionExpired && !AUTH_PAGES.includes(window.location.pathname);
  useEffect(() => {
    if (!shouldRedirect) return;
    const deadline = Date.parse(getStoredValue("session_expires_at") || "");
    reportSessionExpiredShown(!Number.isNaN(deadline) && Date.now() >= deadline ? "deadline" : "auth_failure");

    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (_) {
      // Storage unavailable - still send the user to sign in.
    }
    setInBothStorages("isLoggedIn", "false");
    // No dispatch(setSessionExpired(false)) here: this is a full page navigation, about
    // to unload and re-mount fresh at /signin with clean Redux state anyway. Resetting
    // the flag first would flip shouldRedirect back to false and re-render `children`
    // for a frame before the browser actually navigates away.
    window.location.href = `/signin?${SESSION_EXPIRED_QUERY_PARAM}=true`;
  }, [shouldRedirect]);

  // Render nothing while the redirect above is in flight, same reasoning as the old full-screen
  // gate: the app must not be left mounted and interactive for even one paint.
  if (shouldRedirect) return <div data-testid="session-expired-redirect" />;

  return children;
};

export default SessionExpiredGate;
